/**
 * The browser's own key-value store, or `null` where there is not one to have — and the two-method
 * shape a preference model takes instead of reaching for a global.
 *
 * ── Why the environment is caught here and the MEANING is decided elsewhere ──────────────────
 * Reading the `localStorage` PROPERTY throws before any method is called: Safari's private mode, a
 * browser set to block site data, some embedded contexts. A caller that catches that hands `null`
 * rather than a store that will throw on every touch, so each preference model answers exactly one
 * question — what does a stored string mean — and never has to know whether it is running in a DOM.
 * That is also what lets every one of those models be tested without one.
 *
 * ── Promoted to `shared/` on its second CONSUMER LAYER, 17 September 2026 ────────────────────
 * It was written inside the plan builder (`pages/plan-builder/lib/preferenceStore.ts`) for the day
 * dock's default host and the one-time live-plan note. The navigation panel's collapsed state is
 * the same object again, and it lives in `app/` — which cannot import from `pages/`. So the choice
 * was this file or a third copy of eight lines, and the repo's own second-consumer rule settles it.
 *
 * Nothing about the contract changed in the move. `PreferenceStore` came with it, because a type
 * describing the store belongs beside the store rather than inside whichever model first needed it.
 *
 * ── What belongs behind this, and what does not ──────────────────────────────────────────────
 * Defaults, and only defaults: a density, a collapsed rail, a dismissed one-time note. Never a
 * selection — which day is open, which client is being read — because that is what the URL is for
 * and a link that does not carry it is not a link. `usePlanBuilderSearch`'s header states the same
 * division from the other side.
 *
 * This is NOT the session's storage. `entities/session/model/resilientStorage.ts` wraps a store for
 * Supabase and carries semantics this one must not copy: there, a `null` read means a cross-tab
 * sign-out and a failed read must never remove the key. Here a failed read means "no preference
 * yet", which is the ordinary first visit.
 */

/**
 * The two `localStorage` methods a preference needs. The shape of `window.localStorage`, and of a
 * test's `Map`.
 *
 * Named for what it is rather than for any one preference, because every preference model in the
 * app takes this same object.
 */
export interface PreferenceStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

/**
 * The same store with the third method, for the one model that has to FORGET a key rather than
 * overwrite it: the brand paint hint (`entities/brand-color`), whose absence is what tells the
 * pre-paint script there is nothing to paint. A separate shape rather than a third member on
 * `PreferenceStore`, so the models that only ever read and write keep their two-method fakes.
 */
export interface ForgettingPreferenceStore extends PreferenceStore {
  removeItem(key: string): void;
}

/**
 * The real store, or `null` where the property itself refuses to be read.
 *
 * `null` rather than a throwing stub: a model handed `null` returns its fallback in one branch,
 * where a model handed a store that throws has to wrap every call. The distinction is the whole
 * reason this function exists.
 */
export function preferenceStore(): ForgettingPreferenceStore | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * The same store, scoped to **this tab and this visit** — `sessionStorage`, which is the same two
 * methods and the same throwing property.
 *
 * It is a second function rather than a parameter on the first because the choice between the two
 * is a statement about the VALUE's lifetime, and a boolean argument at the call site says nothing
 * about which lifetime was meant. A preference that should outlive the browser closing takes
 * `preferenceStore`; something that is true of one arrival and stale on the next takes this.
 *
 * Its one caller today is the sign-up hero's photo roll (`authHeroJourney.ts`): rolled once per
 * visit, it has to survive the confirmation e-mail's round-trip back into the SAME tab, and it must
 * not survive into a visit a month later — that would be a stored decision nobody made, kept
 * forever, about a photograph.
 */
export function sessionPreferenceStore(): ForgettingPreferenceStore | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}
